/*
 * The account: Zerodha, capital, the halt, and the instrument list.
 *
 * THE MORNING RITUAL LIVES AT THE TOP, because it is the one thing that has to
 * be done by hand every trading day and everything else depends on it. Zerodha
 * ends every API session overnight and SEBI does not permit a broker to reopen
 * one without a person present, so no amount of automation removes this step.
 * It is stated plainly rather than buried, because somebody who does not know
 * it will conclude the application is broken.
 *
 * The API secret is write-only. It is sent once, encrypted at rest, and never
 * returned — so the field is always blank and saying so is part of the label
 * rather than something to be worked out from an empty box.
 */

import { useState } from "react";

import {
  useDisconnect,
  useForgetAccount,
  useFunds,
  useHoldings,
  useRefreshInstruments,
  useRunNow,
  useSaveCredentials,
  useSetCapital,
  useSetHalted,
  useStatus,
} from "../api/hooks";
import { IconBolt, IconCheck, IconRefresh, IconWarning } from "../components/Icons";
import { useToast } from "../components/Toast";
import { compactRupees, price as fmtPrice, rupees, signedRupees, toneOf } from "../lib/format";

export function SettingsPage() {
  const status = useStatus();
  const account = status.data;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <p>The Zerodha connection, how much may be deployed, and the emergency stop.</p>
        </div>
      </div>

      <div className="grid main-side">
        <div className="col">
          <ZerodhaCard />
          <CapitalCard />
          <InstrumentsCard />
        </div>
        <div className="col">
          <HaltCard />
          {account?.sessionLive && <FundsCard />}
          <LimitsCard />
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────── Zerodha ─────────────────────────── */

function ZerodhaCard() {
  const status = useStatus();
  const save = useSaveCredentials();
  const disconnect = useDisconnect();
  const forget = useForgetAccount();
  const toast = useToast();

  const account = status.data;
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [editing, setEditing] = useState(false);

  const showForm = editing || !account?.connected;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Zerodha</h2>
        {account?.sessionLive ? (
          <span className="pill live"><IconCheck size={11} /> Connected today</span>
        ) : account?.connected ? (
          <span className="pill warn">Session expired</span>
        ) : (
          <span className="pill off">Not connected</span>
        )}
      </div>

      <div className="card-body col">
        {account?.connected && !account.sessionLive && (
          <div className="banner warn" style={{ margin: 0 }}>
            <span className="ico"><IconWarning size={17} /></span>
            <div className="body">
              <strong>Today's session has not been opened</strong>
              <p>
                Zerodha ends every API session overnight, and SEBI does not allow a
                broker to refresh one without a person. This is the only thing that
                needs doing each trading morning — after it, the day runs by itself.
              </p>
            </div>
          </div>
        )}

        {account?.connected && (
          <div className="col" style={{ gap: 0 }}>
            <Row k="Kite user" v={account.kiteUserName ?? account.kiteUserId ?? "—"} />
            <Row k="API key" v={account.apiKey ?? "—"} />
            <Row k="Token issued for" v={account.tokenDate ?? "never"} />
            {/* Shown always, not only while the form is open. When a sign-in
                appears to work at Zerodha and changes nothing here, a redirect
                URL pointing somewhere else is the reason nine times in ten,
                and it is invisible from this side. */}
            <Row k="Redirect URL to set at Zerodha" v={`${window.location.origin}/`} />
          </div>
        )}

        <div className="row">
          {account?.loginUrl && (
            <a className="btn primary" href={account.loginUrl}>
              {account.sessionLive ? "Reconnect to Zerodha" : "Sign in to Zerodha"}
            </a>
          )}
          {account?.connected && (
            <>
              <button className="btn" onClick={() => setEditing((e) => !e)}>
                {editing ? "Cancel" : "Change key"}
              </button>
              <button
                className="btn ghost"
                disabled={disconnect.isPending}
                onClick={() =>
                  disconnect.mutate(undefined, {
                    onSuccess: () => toast.ok("Signed out at Zerodha's end too."),
                    onError: (e) => toast.fail(e),
                  })
                }
              >
                Disconnect
              </button>
            </>
          )}
        </div>

        {showForm && (
          <div className="col" style={{ gap: 11 }}>
            <div className="field">
              <label htmlFor="apiKey">Kite Connect API key</label>
              <input
                id="apiKey"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={account?.apiKey ?? "from developers.kite.trade"}
                autoComplete="off"
              />
            </div>
            <div className="field">
              <label htmlFor="apiSecret">API secret</label>
              <input
                id="apiSecret"
                type="password"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder="not shown once saved"
                autoComplete="off"
              />
              <span className="hint">
                Encrypted before it is stored and never sent back to this page. Changing
                either value drops the current access token, because a token issued
                against one app means nothing to another.
              </span>
            </div>
            <div className="field">
              <span className="hint">
                In your Kite Connect app, set the redirect URL to{" "}
                <code className="mono">{window.location.origin}/</code> — that is where
                Zerodha sends you back with the day's token.
              </span>
            </div>
            <button
              className="btn primary"
              disabled={save.isPending || !apiKey.trim() || !apiSecret.trim()}
              onClick={() =>
                save.mutate(
                  { apiKey: apiKey.trim(), apiSecret: apiSecret.trim() },
                  {
                    onSuccess: () => {
                      toast.ok("Saved. Now sign in to Zerodha.");
                      setApiKey("");
                      setApiSecret("");
                      setEditing(false);
                    },
                    onError: (e) => toast.fail(e),
                  },
                )
              }
            >
              {save.isPending ? <span className="spinner" /> : "Save credentials"}
            </button>
          </div>
        )}

        {account?.connected && (
          <button
            className="btn danger sm"
            style={{ alignSelf: "flex-start" }}
            disabled={forget.isPending}
            onClick={() => {
              if (!window.confirm("Forget this Zerodha account entirely?")) return;
              forget.mutate(undefined, {
                onSuccess: () => toast.ok("Account forgotten."),
                onError: (e) => toast.fail(e),
              });
            }}
          >
            Forget this account
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── capital ─────────────────────────── */

function CapitalCard() {
  const status = useStatus();
  const setCapital = useSetCapital();
  const toast = useToast();
  const account = status.data;

  const [capital, setCapitalValue] = useState<string>("");
  const [dayLoss, setDayLoss] = useState<string>("");
  const [maxTrades, setMaxTrades] = useState<string>("");
  const [paperCapital, setPaperCapital] = useState<string>("");

  const capitalNow = account?.capitalRupees ?? 0;
  const lossNow = account?.accountDailyLossRupees ?? 0;
  const tradesCapNow = account?.accountMaxTradesPerDay ?? 6;
  const tradedToday = account?.tradesToday ?? 0;
  const paperCapitalNow = account?.paperCapitalRupees ?? 1_000_000;
  const paperDeployed = account?.paperDeployed ?? 0;
  const paperTradedToday = account?.paperTradesToday ?? 0;
  const deployed = account?.deployed ?? 0;
  const usedPct = capitalNow ? Math.min(100, (deployed / capitalNow) * 100) : 0;

  if (!account?.connected) return null;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Capital and the account ceilings</h2>
        <span className="sub">{compactRupees(deployed)} of {compactRupees(capitalNow)} deployed</span>
      </div>
      <div className="card-body col">
        <p className="small muted" style={{ marginTop: 0 }}>
          A strategy's own size limits bound only that strategy. Six of them, each
          within its own limits, would happily deploy six times what you have — so
          this is the number that is actually true, and it is checked against
          everything currently held before anything new is opened.
        </p>
        <p className="small muted" style={{ margin: 0 }}>
          <strong>These three apply to live trading only.</strong> Paper draws on its
          own purse below and is not counted here, because a simulated position that
          held part of your real capital would refuse a real trade for want of money
          nobody had spent.
        </p>

        <div
          style={{
            height: 7,
            borderRadius: 5,
            background: "var(--surface-2)",
            overflow: "hidden",
          }}
          role="img"
          aria-label={`${Math.round(usedPct)} per cent of capital deployed`}
        >
          <div
            style={{
              width: `${usedPct}%`,
              height: "100%",
              background: usedPct > 85 ? "var(--warn)" : "var(--accent)",
              transition: "width 400ms ease",
            }}
          />
        </div>

        <div className="grid cols-3">
          <div className="field">
            <label htmlFor="capital">Most that may be deployed (₹)</label>
            <input
              id="capital"
              type="number"
              min={1000}
              step={1000}
              value={capital === "" ? capitalNow : capital}
              onChange={(e) => setCapitalValue(e.target.value)}
            />
            <span className="hint">
              The total that can be held in open positions at once, across every
              strategy. Nothing new opens once it is full.
            </span>
          </div>
          <div className="field">
            <label htmlFor="maxTrades">Most trades in a day</label>
            <input
              id="maxTrades"
              type="number"
              min={1}
              max={50}
              step={1}
              value={maxTrades === "" ? tradesCapNow : maxTrades}
              onChange={(e) => setMaxTrades(e.target.value)}
            />
            <span className="hint">
              Entries across every strategy together. {tradedToday} used today.
              Costs are per trade, so this is the limit that bites before the loss
              limit does.
            </span>
          </div>
          <div className="field">
            <label htmlFor="dayLoss">Halt after losing (₹)</label>
            <input
              id="dayLoss"
              type="number"
              min={100}
              step={500}
              value={dayLoss === "" ? lossNow : dayLoss}
              onChange={(e) => setDayLoss(e.target.value)}
            />
            <span className="hint">
              Realised losses across every strategy in one day. The per-strategy limit
              stops one rule; this stops everything.
            </span>
          </div>
        </div>

        <div
          style={{
            borderTop: "1px solid var(--border)",
            paddingTop: 14,
            marginTop: 4,
          }}
        >
          <div className="row tight mb">
            <strong style={{ fontSize: 13 }}>Paper trading</strong>
            <span className="pill paper">simulated</span>
            <span className="small faint">
              {compactRupees(paperDeployed)} of {compactRupees(paperCapitalNow)} ·{" "}
              {paperTradedToday} trade{paperTradedToday === 1 ? "" : "s"} today
            </span>
          </div>
          <div className="grid cols-2">
            <div className="field">
              <label htmlFor="paperCapital">Simulated purse (₹)</label>
              <input
                id="paperCapital"
                type="number"
                min={1000}
                step={10000}
                value={paperCapital === "" ? paperCapitalNow : paperCapital}
                onChange={(e) => setPaperCapital(e.target.value)}
              />
              <span className="hint">
                Money that does not exist, so it can be as large as you like. Nothing
                else bounds a paper run — no trade count, no loss limit, no position
                cap — because on paper those only cut a rule off before it has shown
                you what it does.
              </span>
            </div>
            <div className="field">
              <span className="hint" style={{ marginTop: 22 }}>
                Every one of those limits comes back the moment a strategy is moved
                to live. Set this near what you would really trade if you want the
                paper P&amp;L to mean something —{" "}
                <strong>a big purse makes big numbers that will not transfer.</strong>
              </span>
            </div>
          </div>
        </div>

        <button
          className="btn primary"
          style={{ alignSelf: "flex-start" }}
          disabled={setCapital.isPending}
          onClick={() =>
            setCapital.mutate(
              {
                capitalRupees: Number(capital === "" ? capitalNow : capital),
                maxDailyLossRupees: Number(dayLoss === "" ? lossNow : dayLoss),
                maxTradesPerDay: Number(maxTrades === "" ? tradesCapNow : maxTrades),
                paperCapitalRupees: Number(
                  paperCapital === "" ? paperCapitalNow : paperCapital,
                ),
              },
              {
                onSuccess: () => {
                  toast.ok("Saved.");
                  setCapitalValue("");
                  setDayLoss("");
                  setMaxTrades("");
                  setPaperCapital("");
                },
                onError: (e) => toast.fail(e),
              },
            )
          }
        >
          {setCapital.isPending ? <span className="spinner" /> : "Save"}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── halt ─────────────────────────── */

function HaltCard() {
  const status = useStatus();
  const halt = useSetHalted();
  const run = useRunNow();
  const toast = useToast();
  const account = status.data;

  if (!account?.connected) return null;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Trading</h2>
        <span className={`pill ${account.halted ? "danger" : "live"}`}>
          {account.halted ? "Halted" : "Armed"}
        </span>
      </div>
      <div className="card-body col">
        {account.halted ? (
          <>
            <p className="small muted" style={{ marginTop: 0 }}>
              {account.haltReason ??
                "Nothing new will be opened. An account starts halted, and stays halted until a person clears it."}
            </p>
            <button
              className="btn success"
              disabled={halt.isPending || !account.sessionLive}
              onClick={() =>
                halt.mutate(
                  { halted: false },
                  {
                    onSuccess: () => toast.ok("Armed. Enabled strategies will run."),
                    onError: (e) => toast.fail(e),
                  },
                )
              }
            >
              {halt.isPending ? <span className="spinner" /> : <IconCheck size={15} />} Arm trading
            </button>
            {!account.sessionLive && (
              <p className="tiny faint" style={{ margin: 0 }}>
                Connect to Zerodha first — arming without a session would do nothing.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="small muted" style={{ marginTop: 0 }}>
              Enabled strategies are running. Nothing further is needed today.
            </p>
            <button
              className="btn danger"
              disabled={halt.isPending}
              onClick={() =>
                halt.mutate(
                  { halted: true, reason: "Halted by hand." },
                  {
                    onSuccess: () => toast.ok("Halted."),
                    onError: (e) => toast.fail(e),
                  },
                )
              }
            >
              <IconBolt size={15} /> Halt trading
            </button>
          </>
        )}

        <div className="switch-row">
          <div>
            <div className="t">Run a tick now</div>
            <div className="d">
              Does not wait for the next scheduled one. Useful for watching what a rule
              decides without sitting through the interval.
            </div>
          </div>
          <button
            className="btn sm"
            disabled={run.isPending}
            onClick={() =>
              run.mutate(undefined, {
                onSuccess: () => toast.ok("Tick run."),
                onError: (e) => toast.fail(e),
              })
            }
          >
            {run.isPending ? <span className="spinner" /> : "Run"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── funds ─────────────────────────── */

function FundsCard() {
  const funds = useFunds(true);
  const holdings = useHoldings(true);

  const available = (funds.data?.available as Record<string, number> | undefined)?.live_balance;
  const net = funds.data?.net as number | undefined;

  return (
    <div className="card">
      <div className="card-head">
        <h2>At Zerodha</h2>
        <span className="sub">their numbers, not ours</span>
      </div>
      <div className="card-body col">
        {funds.isError ? (
          <p className="small faint" style={{ margin: 0 }}>
            {String((funds.error as Error).message)}
          </p>
        ) : (
          <div className="col" style={{ gap: 0 }}>
            <Row k="Available" v={available !== undefined ? rupees(available, 2) : "—"} />
            <Row k="Net" v={net !== undefined ? rupees(net, 2) : "—"} />
          </div>
        )}

        {(holdings.data?.length ?? 0) > 0 && (
          <>
            <div className="tiny faint">Holdings in the account</div>
            <div className="table-wrap" style={{ maxHeight: 240, overflowY: "auto" }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th className="num">Qty</th>
                    <th className="num">Avg</th>
                    <th className="num">P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {holdings.data!.map((h, i) => (
                    <tr key={i}>
                      <td className="sym">{h.tradingsymbol}</td>
                      <td className="num">{h.quantity}</td>
                      <td className="num">{fmtPrice(h.averagePrice)}</td>
                      <td className={`num ${toneOf(h.pnl)}`}>{signedRupees(h.pnl, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="tiny faint" style={{ margin: 0 }}>
              These are everything in the Zerodha account, including whatever was bought
              by hand. Only positions this application opened are managed by it.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── instruments ─────────────────────────── */

function InstrumentsCard() {
  const refresh = useRefreshInstruments();
  const toast = useToast();
  const status = useStatus();

  return (
    <div className="card">
      <div className="card-head"><h2>Instrument list</h2></div>
      <div className="card-body col">
        <p className="small muted" style={{ marginTop: 0 }}>
          Zerodha publishes the tradeable list once a day — around a hundred thousand
          rows for NSE and BSE together. It is cached here so the symbol search is
          instant, and it is worth refreshing when a symbol you expect is missing.
        </p>
        <button
          className="btn"
          style={{ alignSelf: "flex-start" }}
          disabled={refresh.isPending || !status.data?.connected}
          onClick={() =>
            refresh.mutate(undefined, {
              onSuccess: (r) => toast.ok(`${r.count.toLocaleString("en-IN")} instruments loaded.`),
              onError: (e) => toast.fail(e),
            })
          }
        >
          {refresh.isPending ? <span className="spinner" /> : <IconRefresh size={15} />} Refresh now
        </button>
        {refresh.isPending && (
          <p className="tiny faint" style={{ margin: 0 }}>
            This takes a moment — it is a large file.
          </p>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── ceilings ─────────────────────────── */

function LimitsCard() {
  const status = useStatus();
  const account = status.data;

  return (
    <div className="card">
      <div className="card-head"><h2>Server ceilings</h2></div>
      <div className="card-body">
        <p className="small muted" style={{ marginTop: 0 }}>
          Set on the server and not changeable from this screen. Whatever a strategy
          asks for, these are the hard limits.
        </p>
        <div className="col" style={{ gap: 0 }}>
          <Row k="Largest single live order" v={rupees(account?.maxOrderRupees ?? 0)} />
          <Row k="Largest daily loss" v={rupees(account?.maxDailyLossRupees ?? 0)} />
          <Row
            k="Trades used today"
            v={`${account?.tradesToday ?? 0} of ${account?.accountMaxTradesPerDay ?? "—"}`}
          />
          <Row k="Engine" v={account?.engineEnabled ? "running" : "switched off"} />
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="switch-row">
      <div className="t" style={{ fontWeight: 400, color: "var(--text-dim)" }}>{k}</div>
      <div className="num" style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
    </div>
  );
}
